import React, { useContext, useEffect, useRef, useState } from 'react';
import { Context } from '../../../App';
import SeoHelmet from '../../common/SeoHelmet';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Zoom from '@mui/material/Zoom';
import InputAdornment from '@mui/material/InputAdornment';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import MuiSnackbar from '../../common/MuiSnackbar';
import CircularProgress from '@mui/material/CircularProgress';

import CalculateButton from '../../common/calculateButton';
import SaveButton from '../../common/saveButton';
import Header from '../../common/Header';
import Footer from '../../common/Footer';
import Sidebar from '../../sidebar/Sidebar';
import DecisionModal from '../../modals/DecisionModal';
import PricingInfoModal from '../../modals/PricingInfoModal';
import ClayEffectiveFrictionAngleResult from './ClayEffectiveFrictionAngleResult';
import ClayTpmGraphSvg from './ClayTpmGraphSvg';
import ClayKennyGraphSvg from './ClayKennyGraphSvg';
import ClayGibsonGraphSvg from './ClayGibsonGraphSvg';
import { svgContainerToPngBase64 } from '../../../helpers/svgContainerToPngBase64';
import Button from '@mui/material/Button';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

import { useCalculationContext, CalculationContextProvider } from '../../context/calculationContext';
import useLoadSavedCalculation from '../useLoadSavedCalculation';

const ClayEffectiveFrictionAnglePdfCaptureGraphs = React.memo(function ClayEffectiveFrictionAnglePdfCaptureGraphs({
  result,
  tpmGraphRef,
  kennyGraphRef,
  gibsonGraphRef,
}) {
  const { inputs, outputs } = result;
  const piValue = Number(inputs.pi);

  if (!Number.isFinite(piValue)) return null;

  return (
    <>
      <div
        ref={tpmGraphRef}
        style={{
          position: 'absolute',
          left: -99999,
          top: -99999,
          width: 380,
          pointerEvents: 'none',
          overflow: 'hidden',
        }}
      >
        <ClayTpmGraphSvg pi={piValue} phi={Number(outputs.phi_TPM)} showOverlay={piValue >= 5 && piValue <= 1000} />
      </div>
      <div
        ref={kennyGraphRef}
        style={{
          position: 'absolute',
          left: -99999,
          top: -99999,
          width: 380,
          pointerEvents: 'none',
          overflow: 'hidden',
        }}
      >
        <ClayKennyGraphSvg pi={piValue} sinPhi={Number(outputs.sinPhi_Kenny)} showOverlay={piValue >= 7 && piValue <= 115} />
      </div>
      <div
        ref={gibsonGraphRef}
        style={{
          position: 'absolute',
          left: -99999,
          top: -99999,
          width: 380,
          pointerEvents: 'none',
          overflow: 'hidden',
        }}
      >
        <ClayGibsonGraphSvg
          pi={piValue}
          phi={Number(outputs.phi_Gibson)}
          phiRes={Number(outputs.phiRes_Gibson)}
          showOverlay={piValue >= 13 && piValue <= 117}
        />
      </div>
    </>
  );
});

const ClayEffectiveFrictionAngle = () => {
  const { user } = useContext(Context);

  const {
    MAX_NAME_LENGTH,
    isLoadingCalculate,
    isLoadingLoadAll,
    isLoadingLoad,
    isLoadingSave,
    result,
    calculations,
    showOutput,
    setShowOutput,
    calculateButtonDisabled,
    setCalculateButtonDisabled,
    saveButtonDisabled,
    setSaveButtonDisabled,
    errorMessage,
    displaySidebar,
    setDisplaySidebar,
    decisionModalIsOpen,
    decision,
    setDecision,
    handleSidebarDisplay,
    setDecisionModalIsOpenToFalse,
    showMuiSnack,
    open,
    setOpen,
    alertMessage,
    severity,
    onChangeWarningForPositiveDecimals,
    calculate,
    loadOneCalculation,
    loadAllCalculations,
    update,
    checkCalculationNameforSaving,
    checkCalculationNameforSavingAs,
    deleteCalculations,
    calculationCountReached,
    saveCountReached,
  } = useCalculationContext();

  //SET STATES FOR INPUT VARIABLES
  const [name, setName] = useState('');
  const [pi, setPi] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [showPdfWarningModal, setShowPdfWarningModal] = useState(false);
  const tpmGraphRef = useRef(null);
  const kennyGraphRef = useRef(null);
  const gibsonGraphRef = useRef(null);

  // SET THE LOADED INPUTS
  const setInputs = (inputs) => {
    setName(inputs.name);
    setPi(inputs.pi);
  };

  //INPUTS FOR CALCULATION
  const calculationInput = {
    name,
    pi,
  };

  // MANAGE SAVE & CALCULATE BUTTONS DISABLED/ENABLED STATES
  useEffect(() => {
    setCalculateButtonDisabled(false);
    setSaveButtonDisabled(false);

    if ((calculationInput.name || calculationInput.name === 0) && (calculationInput.pi || calculationInput.pi === 0)) {
      setCalculateButtonDisabled(false);
      setSaveButtonDisabled(false);
    } else {
      setCalculateButtonDisabled(true);
      setSaveButtonDisabled(true);
    }
  }, [setCalculateButtonDisabled, setSaveButtonDisabled, calculationInput.name, calculationInput.pi]);

  // Do not render output if any input parameter except name changes
  useEffect(() => {
    setShowOutput(false);
  }, [setShowOutput, calculationInput.pi]);

  //CLEAR INPUTS AND CLEAR OUTPUT
  const createNewCalculation = () => {
    setName('');
    setPi('');
    setShowOutput(false);
  };

  //API URLS
  const URL = {
    CALCULATE: '/api/v1/calculations/clayEffectiveFrictionAngle',
    LOADONE: `/api/v1/calculations/clayEffectiveFrictionAngle/loadOne/`,
    LOADALL: '/api/v1/calculations/clayEffectiveFrictionAngle/loadAll',
    UPDATE: `/api/v1/calculations/clayEffectiveFrictionAngle/update/`,
    SAVE: '/api/v1/calculations/clayEffectiveFrictionAngle/save',
    CHECKUNIQUENAME: `/api/v1/calculations/clayEffectiveFrictionAngle/checkUniqueName/`,
    DELETE: '/api/v1/calculations/clayEffectiveFrictionAngle/delete',
  };
  useLoadSavedCalculation({ URL, loadOneCalculation, calculate, setInputs, showMuiSnack });

  const handleChangeName = (e) => {
    if (e.target.value.length >= MAX_NAME_LENGTH) {
      showMuiSnack(`Calculation name can be max ${MAX_NAME_LENGTH} characters.`, 'warning');
    }
    setName(e.target.value);
  };

  const handleExportPdf = async () => {
    if (isExporting) return;
    if (user.role === 'basic' || user.role === 'standard') {
      setShowPdfWarningModal(true);
      return;
    }

    setIsExporting(true);
    try {
      await new Promise((resolve) => requestAnimationFrame(() => resolve()));

      const [tpmGraph, kennyGraph, gibsonGraph] = await Promise.all([
        svgContainerToPngBase64(tpmGraphRef.current, {
          scale: 2,
          inlineImages: true,
        }),
        svgContainerToPngBase64(kennyGraphRef.current, {
          scale: 2,
          inlineImages: true,
        }),
        svgContainerToPngBase64(gibsonGraphRef.current, {
          scale: 2,
          inlineImages: true,
        }),
      ]);

      if (!tpmGraph || !kennyGraph || !gibsonGraph) {
        showMuiSnack('Graph capture failed. Please recalculate and try again.', 'warning');
        return;
      }

      const payload = {
        ...calculationInput,
        tpmGraph,
        kennyGraph,
        gibsonGraph,
      };

      const res = await fetch('/api/v1/pdf/clayEffectiveFrictionAngle', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error('PDF generation failed');
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'geocalc-clay-effective-friction-angle-report.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    } finally {
      setIsExporting(false);
    }
  };

  //---------------------------------------------//
  // RETURN INPUT AND OUTPUT JSX
  return (
    <>
      <Header />
      <SeoHelmet
        title="GeoCalc | PI vs φ′ Calculator | Clay Effective Friction Angle"
        description="Estimate the effective friction angle (φ′) of clayey soils from Plasticity Index (PI) using relationships by Terzaghi, Peck & Mesri, Kenny, and Gibson."
        url="https://www.geocalc.app/calculations/clayEffectiveFrictionAngle"
        image="https://www.geocalc.app/assets/og-theory-v1.jpg"
        schemaType="tool"
        breadcrumbs={[
          { name: 'Home', url: 'https://www.geocalc.app/' },
          { name: 'PI vs φ′ Calculator', url: 'https://www.geocalc.app/calculations/clayEffectiveFrictionAngle' },
        ]}
      />
      <div className="container">
        <MuiSnackbar open={open} setOpen={setOpen} severity={severity} alertMessage={alertMessage} />
        {displaySidebar ? (
          <Sidebar
            URL={URL}
            loadAllCalculations={loadAllCalculations}
            calculations={calculations}
            loadOneCalculation={loadOneCalculation}
            calculate={calculate}
            setInputs={setInputs}
            deleteCalculations={deleteCalculations}
            createNewCalculation={createNewCalculation}
            isLoading={isLoadingLoadAll}
            handleSidebarDisplay={handleSidebarDisplay}
            displaySidebar={displaySidebar}
            setDisplaySidebar={setDisplaySidebar}
          />
        ) : (
          <MenuOpenIcon
            color="primary"
            fontSize="large"
            className="sidebar-open-icon"
            alt="side bar menu icon"
            label="Show my calculations"
            onClick={handleSidebarDisplay}
          />
        )}
        <DecisionModal
          URL={URL}
          data={calculationInput}
          decisionModalIsOpen={decisionModalIsOpen}
          setDecisionModalIsOpenToFalse={setDecisionModalIsOpenToFalse}
          decision={decision}
          setDecision={setDecision}
          checkCalculationNameforSavingAs={checkCalculationNameforSavingAs}
          name={name}
          setName={setName}
          errorMessage={errorMessage}
          update={update}
          saveButtonDisabled={saveButtonDisabled}
        />
        <PricingInfoModal open={showPdfWarningModal} setOpen={setShowPdfWarningModal} role={user.role} />
        <div className="right-calculation">
          <section className="input">
            <h1>PI vs &phi;' Relationships | Terzaghi, Peck, Mesri 1996 - Kenny 1959 - Gibson 1953</h1>
            <form>
              <TextField
                style={{ minWidth: 80, maxWidth: 120 }}
                size="small"
                label="Name"
                variant="outlined"
                inputProps={{
                  maxLength: `${MAX_NAME_LENGTH}`,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip
                        title={`Your calculation name (Max. ${MAX_NAME_LENGTH} characters)`}
                        arrow
                        placement="bottom"
                        TransitionComponent={Zoom}
                      >
                        <InfoTwoToneIcon fontSize={'string'} />
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                value={name}
                onChange={handleChangeName}
                disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
              />
              <TextField
                style={{ minWidth: 80, maxWidth: 120 }}
                size="small"
                label="PI"
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip
                        title={
                          <>
                            <div>Plasticity index of clay (%).</div>
                            <br />
                            <div>For Terzaghi, Peck, Mesri (1996) &rarr; valid PI values range between 5-1000. </div>
                            <br />
                            <div>For Kenny (1959) &rarr; valid PI values range between 7-115.</div>
                            <br />
                            <div>For Gibson (1953) &rarr; valid PI values range between 13-117.</div>
                            <br />
                            <div>If you use PI values out of those ranges, results may be inaccurate!</div>
                          </>
                        }
                        arrow
                        placement="bottom"
                        TransitionComponent={Zoom}
                      >
                        <InfoTwoToneIcon fontSize={'string'} />
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                value={pi}
                onChange={(e) => onChangeWarningForPositiveDecimals(e, 'Plasticity Index', setPi)}
                disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
              />

              <div className="buttonContainer">
                <CalculateButton
                  calculationCountReached={calculationCountReached}
                  calculate={calculate}
                  calculationInput={calculationInput}
                  URL={URL}
                  calculateButtonDisabled={calculateButtonDisabled}
                  isLoadingCalculate={isLoadingCalculate}
                  isLoadingLoad={isLoadingLoad}
                  isLoadingSave={isLoadingSave}
                />
                <Button
                  variant="contained"
                  startIcon={!isExporting ? <PictureAsPdfIcon /> : null}
                  onClick={handleExportPdf}
                  disabled={!showOutput || isExporting}
                  style={{ width: '200px' }}
                >
                  {isExporting ? (
                    <>
                      <CircularProgress size={18} style={{ marginRight: 8 }} />
                      Creating PDF
                    </>
                  ) : (
                    'Create PDF'
                  )}
                </Button>
                {isLoadingLoad && <CircularProgress size={30} />}
                <SaveButton
                  saveCountReached={saveCountReached}
                  checkCalculationNameforSaving={checkCalculationNameforSaving}
                  calculationInput={calculationInput}
                  URL={URL}
                  saveButtonDisabled={saveButtonDisabled}
                  isLoadingCalculate={isLoadingCalculate}
                  isLoadingLoad={isLoadingLoad}
                  isLoadingSave={isLoadingSave}
                />
              </div>
            </form>
          </section>
          {showOutput && <ClayEffectiveFrictionAngleResult result={result} />}
          {showOutput && (
            <ClayEffectiveFrictionAnglePdfCaptureGraphs
              result={result}
              tpmGraphRef={tpmGraphRef}
              kennyGraphRef={kennyGraphRef}
              gibsonGraphRef={gibsonGraphRef}
            />
          )}
          {isLoadingCalculate && (
            <div className="centerLoading">
              <CircularProgress />
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

const ClayEffectiveFrictionAngleWithProvider = () => {
  return (
    <CalculationContextProvider>
      <ClayEffectiveFrictionAngle />
    </CalculationContextProvider>
  );
};
export default ClayEffectiveFrictionAngleWithProvider;
